import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as purifyModule from 'dompurify';
import { resolveDomPurify } from './notification-safe-html.pipe';

// Card #674 review: a second safety net after the hand-built escaping below (see #676 for why the
// module is resolved this way). Without a working DOMPurify nothing is trusted.
const DOMPurify = resolveDomPurify(purifyModule);

@Pipe({
    name: 'stripHtml',
    standalone: false
})
export class StripHtmlPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  /** The pixel width an element declares (width="200", width="200px" or style="width: 200px" / style="max-width: 200px"), or null. */
  static elementWidth(el: Element): number | null {
    const attr = (el.getAttribute('width') || '').trim();
    const fromAttr = /^(\d{1,4})(px)?$/i.exec(attr);
    const style = el.getAttribute('style') || '';
    const fromStyle = /(?:^|;)\s*width\s*:\s*(\d{1,4})px/i.exec(style);
    const fromMaxStyle = /(?:^|;)\s*max-width\s*:\s*(\d{1,4})px/i.exec(style);

    const styleWidth = fromStyle ? Number(fromStyle[1]) : null;
    const styleMaxWidth = fromMaxStyle ? Number(fromMaxStyle[1]) : null;
    const attrWidth = fromAttr ? Number(fromAttr[1]) : null;

    if (styleWidth && styleMaxWidth) {
      const minW = Math.min(styleWidth, styleMaxWidth);
      return minW > 0 ? minW : null;
    }
    const n = styleMaxWidth || styleWidth || attrWidth || 0;
    return n > 0 ? n : null;
  }

  /** The pixel width an email gives an image, checking the image itself, its enclosing table/cell, or logo fallback. */
  static imageWidth(img: Element): number | null {
    const direct = StripHtmlPipe.elementWidth(img);
    if (direct) {
      return direct;
    }

    // Check enclosing container (td, th, table, div) for an intended width
    let parent = img.parentElement;
    while (parent && parent.tagName !== 'BODY') {
      const tag = parent.tagName.toUpperCase();
      if (['TD', 'TH', 'TABLE', 'DIV'].includes(tag)) {
        const pw = StripHtmlPipe.elementWidth(parent);
        if (pw && pw > 0) {
          return pw;
        }
      }
      parent = parent.parentElement;
    }

    // If it's a logo or Osmos header and no width is declared, default to 200px so it doesn't blow up the reading pane
    const src = (img.getAttribute('src') || '').toLowerCase();
    const alt = (img.getAttribute('alt') || '').toLowerCase();
    const className = (img.getAttribute('class') || '').toLowerCase();
    if (src.includes('logo') || alt.includes('logo') || className.includes('logo') || src.includes('osmos')) {
      return 200;
    }

    return null;
  }

  // Card #674 review: this pipe rebuilds the email as a string and the result is trusted with
  // bypassSecurityTrustHtml, so EVERYTHING taken from the email must be escaped here. Text that
  // read "&lt;img onerror=...&gt;" came back from the parser as real markup, and a quote in a src or
  // href broke out of the attribute.
  static escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** An image address an email may show (web, inline attachment, or embedded raster image), escaped; else null. */
  static safeImageSrc(src: string | null): string | null {
    const s = (src || '').trim();
    return /^(https?:|cid:|data:image\/(png|jpe?g|gif|webp);)/i.test(s) ? StripHtmlPipe.escapeHtml(s) : null;
  }

  /** Resolves cid: reference using mail attachments if available, otherwise returns safeImageSrc */
  static resolveImageSrc(src: string | null, attachments?: any[]): string | null {
    const raw = (src || '').trim();
    if (/^cid:/i.test(raw)) {
      const cid = raw.slice(4).trim().replace(/^<|>$/g, '');
      if (attachments && Array.isArray(attachments)) {
        const match = attachments.find((a: any) => {
          if (!a) return false;
          const attCid = (a.cid || a.contentId || '').toString().trim().replace(/^<|>$/g, '');
          return attCid === cid || a.filename === cid;
        });
        if (match && match.content && match.content.data) {
          try {
            const data = Uint8Array.from(match.content.data);
            let binary = '';
            for (let i = 0; i < data.length; i++) {
              binary += String.fromCharCode(data[i]);
            }
            const base64 = typeof btoa !== 'undefined' ? btoa(binary) : Buffer.from(data).toString('base64');
            const mime = match.contentType || 'image/png';
            return `data:${mime};base64,${base64}`;
          } catch (e) {
            // fallback to safeImageSrc
          }
        }
      }
    }
    return StripHtmlPipe.safeImageSrc(raw);
  }

  /** A link an email may carry (web, mail, phone), escaped; else null. */
  static safeHref(href: string | null): string | null {
    const s = (href || '').trim();
    return /^(https?:|mailto:|tel:)/i.test(s) ? StripHtmlPipe.escapeHtml(s) : null;
  }

  /**
   * Transforms an HTML string into plain text by stripping all tags and styles.
   * 
   * @param value The HTML string to transform.
   * @param attachments Optional mail attachments for resolving cid: images.
   * @returns The plain text content or SafeHtml.
   */
  transform(value: string, attachments?: any[]): SafeHtml {
    if (!value) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');
    
    // Define tags to keep (basic structure, images, and tables)
    const allowedTags = ['IMG', 'BR', 'A', 'P', 'UL', 'OL', 'LI', 'HR', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'];
    const headerTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

    const sanitize = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Collapse multiple spaces but keep single space
        return StripHtmlPipe.escapeHtml((node.textContent ?? '').replace(/\s+/g, ' '));
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toUpperCase();

        // 1. Handle Images
        if (tagName === 'IMG') {
          const rawSrc = element.getAttribute('src');
          // Drop tracking pixels (1x1 images)
          const attrW = (element.getAttribute('width') || '').trim();
          const attrH = (element.getAttribute('height') || '').trim();
          const style = element.getAttribute('style') || '';
          const isTrackingPixel = (attrW === '1' && attrH === '1') ||
            /(?:^|;)\s*display\s*:\s*none/i.test(style) ||
            (/(?:^|;)\s*width\s*:\s*1px/i.test(style) && /(?:^|;)\s*height\s*:\s*1px/i.test(style));
          if (isTrackingPixel) {
            return '';
          }

          const src = StripHtmlPipe.resolveImageSrc(rawSrc, attachments);
          // Keep the width the email asked for (digits only, capped at 100%)
          const width = StripHtmlPipe.imageWidth(element);
          const widthAttr = width ? ` width="${width}"` : '';
          return src ? `<img src="${src}"${widthAttr} style="max-width: 100%; height: auto; display: block; margin: 10px 0;">` : '';
        }

        // 2. Process children
        let childrenContent = '';
        for (let i = 0; i < node.childNodes.length; i++) {
          childrenContent += sanitize(node.childNodes[i]);
        }

        // 3. Normalize Tags
        if (headerTags.includes(tagName)) {
          // Convert headers to bold text with same font size to prevent "big" text
          return `<div style="font-weight: bold; margin: 10px 0; font-size: inherit;">${childrenContent}</div>`;
        }

        if (tagName === 'A') {
          const href = StripHtmlPipe.safeHref(element.getAttribute('href'));
          // An unsafe or missing link keeps its text, without the link.
          return href
            ? `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #03C0FC; text-decoration: underline;">${childrenContent}</a>`
            : childrenContent;
        }

        if (['P', 'DIV', 'LI'].includes(tagName)) {
          // Use simple margin-less divs or br to avoid "space line after line"
          return childrenContent.trim() ? `<div style="margin: 0; padding: 0;">${childrenContent}</div>` : '';
        }

        if (tagName === 'TABLE') {
          return `<table style="border-collapse: collapse; margin: 15px 0;">${childrenContent}</table>`;
        }

        if (tagName === 'TR') {
          return `<tr>${childrenContent}</tr>`;
        }

        if (tagName === 'TD' || tagName === 'TH') {
          const isHeader = tagName === 'TH';
          const baseStyle = isHeader 
            ? 'font-weight: bold; text-align: left; background-color: #f8f9fa;' 
            : '';
          return `<${tagName.toLowerCase()} style="${baseStyle} padding: 0px !important; vertical-align: top;">${childrenContent}</${tagName.toLowerCase()}>`;
        }
        
        if (tagName === 'BR') {
          return '<br>';
        }
        
        if (allowedTags.includes(tagName)) {
          return `<${tagName.toLowerCase()}>${childrenContent}</${tagName.toLowerCase()}>`;
        }

        return childrenContent;
      }

      return '';
    };

    let cleanedHtml = '';
    for (let i = 0; i < doc.body.childNodes.length; i++) {
        cleanedHtml += sanitize(doc.body.childNodes[i]);
    }

    const styleBlock = `
      <style>
        .stripped-html-container { max-width: 680px; overflow-x: auto; word-wrap: break-word; }
        .stripped-html-container table { max-width: 100%; width: 100%; border-collapse: collapse; margin: 15px 0; }
        .stripped-html-container th { font-weight: bold; text-align: left; background-color: #f8f9fa; padding: 0px !important; }
        .stripped-html-container td { padding: 0px !important; vertical-align: top; }
        .stripped-html-container img { max-width: 100%; height: auto; object-fit: contain; }
        .stripped-html-container img[src*="logo" i], .stripped-html-container img[alt*="logo" i], .stripped-html-container img[src*="osmos" i] { max-width: 240px; height: auto; }
      </style>
    `;

    // Final cleanup: Remove excessive consecutive line breaks and empty tags
    const result = cleanedHtml
      .replace(/(<div style="margin: 0; padding: 0;">\s*<\/div>)+/gi, '')
      .replace(/(<br\s*\/?>\s*)+/gi, '<br>') // Collapse consecutive <br> tags (with optional spaces) into one
      .replace(/\s+/g, ' ')
      .trim();

    // Justification for bypassSecurityTrustHtml: every value taken from the email is escaped or
    // allow-listed above, and the whole string then goes through DOMPurify (defense in depth).
    // Angular's own sanitizer would strip the <style> block and inline styles this view needs.
    if (!DOMPurify) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    const purified = DOMPurify.sanitize(`<div class="stripped-html-container">${styleBlock}${result}</div>`, {
      FORCE_BODY: true, // keep the leading <style> block
      ADD_ATTR: ['target'],
    });
    return this.sanitizer.bypassSecurityTrustHtml(purified);
  }
}
